import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MomentumBadge = ({ label }) => {
  if (label === 'HEATING') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800">HEATING</span>;
  if (label === 'COOLING') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">COOLING</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500">FLAT</span>;
};

const StatusBadge = ({ status }) => {
  const cls = status === 'HOT NOW' ? 'bg-red-100 text-red-800' : status === 'COOLING' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{status}</span>;
};

const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

export default function Window21DecayFrequency() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [decayData, setDecayData] = useState(null);
  const [rwData, setRwData] = useState(null);
  const [btData, setBtData] = useState(null);
  const [windowSize, setWindowSize] = useState('20');
  const [mainTab, setMainTab] = useState("summary");

  useEffect(() => {
    (async () => {
      try {
        const [decayRes, rwRes, btRes] = await Promise.all([
          axios.get(`${API}/analysis/decay-frequency`),
          axios.get(`${API}/analysis/rolling-window`),
          axios.get(`${API}/analysis/momentum-backtest`),
        ]);
        setDecayData(decayRes.data);
        setRwData(rwRes.data);
        setBtData(btRes.data);
      } catch { toast.error('Failed to fetch data'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 21</h1><p className="text-xs text-gray-500">Decay Frequency + Rolling Window + Predicted Next Ball</p></div>
            </div>
            <div className="flex items-center gap-2">
              {mainTab !== "summary" && (
                <>
                  <span className="text-xs font-bold text-gray-500">Window:</span>
                  <Select value={windowSize} onValueChange={setWindowSize}>
                    <SelectTrigger className="w-32" data-testid="window-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">Last 10</SelectItem>
                      <SelectItem value="20">Last 20</SelectItem>
                      <SelectItem value="50">Last 50</SelectItem>
                      <SelectItem value="100">Last 100</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              <ExportButton data={decayData} filename="w21_analysis" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <WindowNarrative windowId="W21" {...NARRATIVES.W21} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : (
          <>
            {/* Main Tab Switcher */}
            <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 mb-6" data-testid="main-tab-bar">
              <button onClick={() => setMainTab("summary")} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === "summary" ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-100"}`} data-testid="tab-summary">
                Predicted Next Ball
              </button>
              <button onClick={() => setMainTab("backtest")} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === "backtest" ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-100"}`} data-testid="tab-backtest">
                2026 Momentum Backtest
              </button>
              {Object.entries(ballLabels).map(([k, l]) => (
                <button key={k} onClick={() => setMainTab(k)} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === k ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-100"}`} data-testid={`tab-${k}`}>
                  {l}
                </button>
              ))}
            </div>

            {/* Summary Tab */}
            {mainTab === "summary" && <PredictionSummary decayData={decayData} rwData={rwData} windowSize={windowSize} />}

            {/* Backtest Tab */}
            {mainTab === "backtest" && <MomentumBacktest data={btData} rwData={rwData} windowSize={windowSize} />}

            {/* Per-Ball Tabs */}
            {Object.keys(ballLabels).includes(mainTab) && (
              <BallAnalysis ballKey={mainTab} label={ballLabels[mainTab]} decayData={decayData} rwData={rwData} windowSize={windowSize} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

/* ========== PREDICTION SUMMARY ========== */

/* ========== MOMENTUM BACKTEST ========== */
function MomentumBacktest({ data, rwData, windowSize }) {
  if (!data) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;

  const bkeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const bLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };
  const bShort = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };
  const momColor = (m) => m === 'HEATING' ? 'bg-orange-500 text-white' : m === 'COOLING' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600';
  const mid = data.first_half_count;

  // Get current momentum numbers per ball from rolling window
  const currentMomentum = {};
  bkeys.forEach(bk => {
    const nums = rwData?.balls?.[bk]?.[windowSize || '20'] || [];
    const heating = nums.filter(n => n.momentum_label === 'HEATING').sort((a, b) => b.pred_score - a.pred_score);
    const flat = nums.filter(n => n.momentum_label === 'FLAT').sort((a, b) => b.pred_score - a.pred_score);
    const cooling = nums.filter(n => n.momentum_label === 'COOLING').sort((a, b) => b.pred_score - a.pred_score);
    currentMomentum[bk] = { heating, flat, cooling };
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-700 rounded-xl p-6 text-white" data-testid="backtest-header">
        <h2 className="text-2xl font-bold mb-1">2026 Momentum Backtest</h2>
        <p className="text-sm text-amber-100">For each 2026 draw, what was the momentum status of the drawn number at the time? First half ({data.first_half_count} draws) = more random. Second half ({data.second_half_count} draws) = HEATING dominates.</p>
      </div>

      {/* Current Momentum Numbers per Ball */}
      <div className="space-y-4" data-testid="current-momentum">
        {bkeys.map(bk => {
          const cm = currentMomentum[bk];
          return (
            <div key={bk} className="bg-white border rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 flex items-center justify-between">
                <span className="font-bold">{bLabels[bk]}</span>
                <div className="flex gap-3 text-[10px]">
                  <span className="bg-orange-400/30 px-2 py-0.5 rounded">HEATING: {cm.heating.length}</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded">FLAT: {cm.flat.length}</span>
                  <span className="bg-blue-400/30 px-2 py-0.5 rounded">COOLING: {cm.cooling.length}</span>
                </div>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-2.5">
                  <div className="text-[10px] font-bold text-orange-700 uppercase mb-2">HEATING ({cm.heating.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cm.heating.map(n => (
                      <span key={n.number} className="inline-flex w-8 h-8 rounded-full bg-orange-500 text-white items-center justify-center font-mono font-bold text-xs">{n.number}</span>
                    ))}
                    {cm.heating.length === 0 && <span className="text-[10px] text-gray-400">None</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                  <div className="text-[10px] font-bold text-gray-600 uppercase mb-2">FLAT ({cm.flat.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cm.flat.map(n => (
                      <span key={n.number} className="inline-flex w-8 h-8 rounded-full bg-gray-400 text-white items-center justify-center font-mono font-bold text-xs">{n.number}</span>
                    ))}
                    {cm.flat.length === 0 && <span className="text-[10px] text-gray-400">None</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-2.5">
                  <div className="text-[10px] font-bold text-blue-700 uppercase mb-2">COOLING ({cm.cooling.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cm.cooling.map(n => (
                      <span key={n.number} className="inline-flex w-8 h-8 rounded-full bg-blue-500 text-white items-center justify-center font-mono font-bold text-xs">{n.number}</span>
                    ))}
                    {cm.cooling.length === 0 && <span className="text-[10px] text-gray-400">None</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Comparison: First Half vs Second Half */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-bold text-gray-900 mb-1">First Half — Draws 1-{mid}</h3>
          <p className="text-[10px] text-gray-500 mb-3">Early 2026 — more random, mixed momentum</p>
          <div className="overflow-x-auto" data-testid="first-half-summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b bg-gray-50">
                  <th className="text-left py-2 px-2">Ball</th>
                  <th className="text-center py-2 px-2">HEATING</th>
                  <th className="text-center py-2 px-2">FLAT</th>
                  <th className="text-center py-2 px-2">COOLING</th>
                </tr>
              </thead>
              <tbody>
                {bkeys.map(bk => {
                  const s = data.first_half_summary?.[bk]?.position || {};
                  return (
                    <tr key={bk} className="border-b border-gray-100">
                      <td className="py-2 px-2 font-bold text-gray-800">{bLabels[bk]}</td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-orange-600">{s.HEATING?.pct || 0}%</span></td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-gray-500">{s.FLAT?.pct || 0}%</span></td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-blue-600">{s.COOLING?.pct || 0}%</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-bold text-gray-900 mb-1">Second Half — Draws {mid + 1}-{data.total_2026_draws}</h3>
          <p className="text-[10px] text-gray-500 mb-3">Recent 2026 — HEATING numbers drawn more often</p>
          <div className="overflow-x-auto" data-testid="second-half-summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b bg-gray-50">
                  <th className="text-left py-2 px-2">Ball</th>
                  <th className="text-center py-2 px-2">HEATING</th>
                  <th className="text-center py-2 px-2">FLAT</th>
                  <th className="text-center py-2 px-2">COOLING</th>
                </tr>
              </thead>
              <tbody>
                {bkeys.map(bk => {
                  const s = data.second_half_summary?.[bk]?.position || {};
                  const fh = data.first_half_summary?.[bk]?.position || {};
                  const heatDiff = ((s.HEATING?.pct || 0) - (fh.HEATING?.pct || 0)).toFixed(1);
                  return (
                    <tr key={bk} className="border-b border-gray-100">
                      <td className="py-2 px-2 font-bold text-gray-800">{bLabels[bk]}</td>
                      <td className="py-2 px-2 text-center">
                        <span className="font-mono font-bold text-orange-600">{s.HEATING?.pct || 0}%</span>
                        <span className={`ml-1 text-[9px] font-bold ${heatDiff > 0 ? "text-emerald-600" : heatDiff < 0 ? "text-red-500" : "text-gray-400"}`}>{heatDiff > 0 ? "+" : ""}{heatDiff}</span>
                      </td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-gray-500">{s.FLAT?.pct || 0}%</span></td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-blue-600">{s.COOLING?.pct || 0}%</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Draw-by-draw detail */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-bold text-gray-700">Draw-by-Draw Momentum (2026)</h3>
          <p className="text-[10px] text-gray-500">Each cell shows what momentum the drawn number had at that point. Orange = HEATING, Blue = COOLING, Gray = FLAT.</p>
        </div>
        <div className="overflow-x-auto" data-testid="draw-by-draw">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2 px-3 text-[10px] uppercase text-gray-500">#</th>
                <th className="text-left py-2 px-3 text-[10px] uppercase text-gray-500">Date</th>
                {bkeys.map(bk => (
                  <th key={bk} className="text-center py-2 px-2 text-[10px] uppercase text-gray-500" colSpan={2}>{bShort[bk]}</th>
                ))}
              </tr>
              <tr className="bg-gray-50 border-b text-[9px] text-gray-400">
                <th></th><th></th>
                {bkeys.map(bk => (
                  <React.Fragment key={bk}>
                    <th className="text-center px-1">Num</th>
                    <th className="text-center px-1">Status</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.draws || []).map((dr, i) => {
                const isSecondHalf = i >= mid;
                return (
                  <tr key={i} className={`border-b border-gray-100 ${isSecondHalf ? "" : "bg-gray-50/50"}`}>
                    <td className="py-1.5 px-3 font-bold text-gray-500">{i + 1}</td>
                    <td className="py-1.5 px-3 font-bold text-gray-700 whitespace-nowrap">
                      {dr.draw_date}
                      {i === mid - 1 && <span className="ml-1 text-[8px] bg-amber-100 text-amber-700 px-1 rounded">MID</span>}
                    </td>
                    {bkeys.map(bk => {
                      const bm = dr.balls?.[bk] || {};
                      return (
                        <React.Fragment key={bk}>
                          <td className="py-1.5 px-1 text-center">
                            <span className="inline-flex w-7 h-7 rounded-full bg-blue-600 text-white items-center justify-center font-mono font-bold text-[10px]">{bm.number}</span>
                          </td>
                          <td className="py-1.5 px-1 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${momColor(bm.position_momentum)}`}>
                              {bm.position_momentum === 'HEATING' ? 'H' : bm.position_momentum === 'COOLING' ? 'C' : 'F'}
                            </span>
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insight */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-5 text-white" data-testid="backtest-insight">
        <h3 className="font-bold text-lg mb-2">Key Insight</h3>
        <p className="text-sm text-emerald-100">
          In the first {data.first_half_count} draws of 2026, drawn numbers were more randomly distributed across HEATING/FLAT/COOLING.
          In the recent {data.second_half_count} draws, <strong>HEATING numbers appeared significantly more often</strong> — confirming that momentum-based selection improves accuracy as the year progresses.
          Strategy: <strong>Prioritize HEATING numbers from both Decay and Rolling Window analyses.</strong>
        </p>
      </div>
    </div>
  );
}


function PredictionSummary({ decayData, rwData, windowSize }) {
  if (!decayData || !rwData) return null;

  const combined = {};
  Object.keys(ballLabels).forEach(bk => {
    const decayNums = decayData.balls?.[bk] || [];
    const rwNums = rwData.balls?.[bk]?.[windowSize] || [];

    // Build score maps
    const decayMap = {};
    decayNums.forEach((n, i) => { decayMap[n.number] = { rank: i + 1, score: n.pred_score, status: n.status, momentum: n.half_momentum_label }; });
    const rwMap = {};
    rwNums.forEach((n, i) => { rwMap[n.number] = { rank: i + 1, score: n.pred_score, momentum: n.momentum_label }; });

    // Merge: combine scores from both (50/50 weight)
    const allNums = new Set([...Object.keys(decayMap), ...Object.keys(rwMap)].map(Number));
    const merged = [];
    allNums.forEach(num => {
      const d = decayMap[num] || { rank: 50, score: 0, status: 'STEADY', momentum: 'FLAT' };
      const r = rwMap[num] || { rank: 50, score: 0, momentum: 'FLAT' };
      const combinedScore = (d.score * 0.5) + (r.score * 0.5);
      const bothHot = (d.momentum === 'HEATING' && r.momentum === 'HEATING');
      merged.push({
        number: num,
        decay_score: d.score, decay_rank: d.rank, decay_status: d.status, decay_momentum: d.momentum,
        rw_score: r.score, rw_rank: r.rank, rw_momentum: r.momentum,
        combined_score: Math.round(combinedScore * 100) / 100,
        both_hot: bothHot,
      });
    });
    merged.sort((a, b) => b.combined_score - a.combined_score);
    combined[bk] = merged.slice(0, 10);
  });

  return (
    <div className="space-y-6">
      {/* =============================================================
          DECAY-ONLY PREDICTION CARD — pure exponentially-decayed
          frequency score, no rolling-window merge. Same algorithm as the
          "Decay-only" backtest window in W50. Rendered above the combined
          card so users can compare the two side-by-side.
          ============================================================= */}
      <div className="bg-gradient-to-r from-violet-800 to-purple-900 rounded-xl p-6 text-white" data-testid="prediction-header-decay">
        <div className="flex items-center gap-3 mb-3">
          <Target size={28} weight="bold" />
          <h2 className="text-2xl font-bold">Predicted Next Ball — Decay-only</h2>
        </div>
        <p className="text-sm text-white/70">Top 10 numbers per position using <strong>Decay Frequency</strong> alone (exponentially-weighted history). No Rolling Window blend. Historically higher top-10 hit rate — see W50 for the comparison.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(ballLabels).map(([bk, label]) => {
          const decayNums = (decayData.balls?.[bk] || []).slice()
            .sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0))
            .slice(0, 10);
          return (
            <div key={bk} className="bg-white border border-violet-200 rounded-lg overflow-hidden" data-testid={`pred-decay-${bk}`}>
              <div className="bg-gradient-to-r from-violet-600 to-purple-700 text-white px-4 py-3">
                <h3 className="font-bold text-lg">{label}</h3>
                <p className="text-[10px] text-violet-200">Top 10 (Decay-only)</p>
              </div>
              <div className="p-2">
                {decayNums.map((n, i) => (
                  <div key={n.number} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${i === 0 ? "bg-amber-50 border border-amber-200" : i < 3 ? "bg-violet-50" : ""}`}>
                    <span className="text-[10px] font-bold text-gray-400 w-4">{i + 1}</span>
                    <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm text-white ${i === 0 ? "bg-amber-500" : i < 3 ? "bg-violet-600" : "bg-gray-500"}`}>{n.number}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black text-gray-800">{n.pred_score}</div>
                      <div className="text-[8px] text-violet-600">Decay</div>
                    </div>
                    <MomentumBadge label={n.half_momentum_label} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Pick — Decay-only top-1 per position */}
      <div className="bg-gradient-to-r from-violet-700 to-purple-800 rounded-xl p-5 text-white" data-testid="quick-pick-decay">
        <div className="flex items-center gap-3 mb-3">
          <Star size={24} weight="fill" className="text-amber-300" />
          <h3 className="text-xl font-bold">Top Pick (Decay-only) — #1 from each position</h3>
        </div>
        <div className="flex items-center justify-center gap-4">
          {Object.entries(ballLabels).map(([bk, label]) => {
            const decayNums = (decayData.balls?.[bk] || []).slice()
              .sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0));
            const top = decayNums[0];
            return top ? (
              <div key={bk} className="flex flex-col items-center gap-1" data-testid={`quick-pick-decay-${bk}`}>
                <span className="inline-flex w-14 h-14 rounded-full items-center justify-center bg-white text-violet-800 font-mono font-black text-xl">{top.number}</span>
                <span className="text-[10px] text-violet-200 font-bold">{label}</span>
                <span className="text-[9px] text-violet-300">{top.pred_score}</span>
              </div>
            ) : null;
          })}
        </div>
      </div>

      {/* =============================================================
          COMBINED PREDICTION CARD (Decay + Rolling 50/50) — retained
          below the Decay-only card so users can compare both approaches.
          ============================================================= */}
      <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-xl p-6 text-white" data-testid="prediction-header">
        <div className="flex items-center gap-3 mb-3">
          <Target size={28} weight="bold" />
          <h2 className="text-2xl font-bold">Predicted Next Ball — Combined Analysis</h2>
        </div>
        <p className="text-sm text-white/70">Top 10 numbers per position, combining <strong>Decay Frequency</strong> (exponential weighting) + <strong>Rolling Window</strong> (last {windowSize} draws). 50/50 weighted merge.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(ballLabels).map(([bk, label]) => {
          const nums = combined[bk] || [];
          return (
            <div key={bk} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`pred-${bk}`}>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3">
                <h3 className="font-bold text-lg">{label}</h3>
                <p className="text-[10px] text-blue-200">Top 10 predicted</p>
              </div>
              <div className="p-2">
                {nums.map((n, i) => (
                  <div key={n.number} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${i === 0 ? "bg-amber-50 border border-amber-200" : i < 3 ? "bg-blue-50" : ""} ${n.both_hot ? "ring-1 ring-orange-300" : ""}`}>
                    <span className="text-[10px] font-bold text-gray-400 w-4">{i + 1}</span>
                    <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm text-white ${i === 0 ? "bg-amber-500" : i < 3 ? "bg-blue-600" : "bg-gray-500"}`}>{n.number}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black text-gray-800">{n.combined_score}</div>
                      <div className="flex gap-1 text-[8px]">
                        <span className="text-violet-600">D:{n.decay_score}</span>
                        <span className="text-blue-600">R:{n.rw_score}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <MomentumBadge label={n.decay_momentum} />
                      {n.both_hot && <span className="text-[8px] font-bold text-orange-600">BOTH HOT</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Pick Row */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-6 text-white" data-testid="quick-pick">
        <div className="flex items-center gap-3 mb-4">
          <Star size={24} weight="fill" className="text-amber-300" />
          <h3 className="text-xl font-bold">Top Pick — #1 from each position</h3>
        </div>
        <div className="flex items-center justify-center gap-4">
          {Object.entries(ballLabels).map(([bk, label]) => {
            const top = (combined[bk] || [])[0];
            return top ? (
              <div key={bk} className="flex flex-col items-center gap-1">
                <span className="inline-flex w-14 h-14 rounded-full items-center justify-center bg-white text-emerald-800 font-mono font-black text-xl">{top.number}</span>
                <span className="text-[10px] text-emerald-200 font-bold">{label}</span>
                <span className="text-[9px] text-emerald-300">{top.combined_score}</span>
              </div>
            ) : null;
          })}
        </div>
      </div>

      {/* W21 Cross-Ball Count — Total times each number predicted */}
      <div className="bg-white border border-orange-200 rounded-lg overflow-hidden" data-testid="w21-number-count">
        <div className="p-4 border-b border-orange-200 bg-gradient-to-r from-orange-500 to-amber-600 text-white">
          <h3 className="font-bold text-lg">W21 Momentum — Total Times Predicted</h3>
          <p className="text-[10px] text-orange-100 mt-0.5">How many ball positions (out of 5) predict each number in their top 10</p>
        </div>
        <div className="p-4">
          {(() => {
            const numCount = {};
            const numBalls = {};
            Object.entries(ballLabels).forEach(([bk, label]) => {
              (combined[bk] || []).forEach(n => {
                numCount[n.number] = (numCount[n.number] || 0) + 1;
                if (!numBalls[n.number]) numBalls[n.number] = [];
                numBalls[n.number].push(label);
              });
            });
            const sorted = Object.entries(numCount)
              .map(([num, count]) => ({ number: parseInt(num), count, balls: numBalls[parseInt(num)] || [] }))
              .sort((a, b) => b.count - a.count || a.number - b.number);

            return (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-orange-50">
                    <tr>
                      <th className="px-2 py-1.5 text-center font-bold text-gray-500">#</th>
                      <th className="px-2 py-1.5 text-center font-bold text-gray-500">Times</th>
                      <th className="px-2 py-1.5 text-left font-bold text-gray-500">Positions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s, idx) => (
                      <tr key={s.number} className={`${s.count >= 4 ? 'bg-red-50' : s.count >= 3 ? 'bg-orange-50' : s.count >= 2 ? 'bg-amber-50/50' : idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                        <td className="px-2 py-1 text-center">
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs text-white ${s.count >= 4 ? 'bg-red-600' : s.count >= 3 ? 'bg-orange-500' : s.count >= 2 ? 'bg-amber-500' : 'bg-gray-400'}`}>{s.number}</span>
                        </td>
                        <td className={`px-2 py-1 text-center font-black ${s.count >= 4 ? 'text-red-700' : s.count >= 3 ? 'text-orange-700' : s.count >= 2 ? 'text-amber-700' : 'text-gray-500'}`}>{s.count}</td>
                        <td className="px-2 py-1 text-[10px] text-gray-600">{s.balls.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/* ========== PER-BALL ANALYSIS ========== */
function BallAnalysis({ ballKey, label, decayData, rwData, windowSize }) {
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });
  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  const decayNums = decayData?.balls?.[ballKey] || [];
  const rwNums = rwData?.balls?.[ballKey]?.[windowSize] || [];

  const sortedDecay = [...decayNums].sort((a, b) => {
    let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
    return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
  });
  const sortedRw = [...rwNums].sort((a, b) => {
    let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
    return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
  });

  const topDecay = new Set(decayNums.slice(0, 5).map(n => n.number));
  const topRw = new Set([...rwNums].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Decay Frequency */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-violet-600 to-purple-700 text-white px-4 py-3">
            <h3 className="font-bold">{label} — Decay Frequency</h3>
            <p className="text-[10px] text-violet-200">Exponential decay weighting (recent draws matter more)</p>
          </div>
          <div className="overflow-x-auto" data-testid="decay-table">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50">
                <tr>
                  {[['number','#'],['weighted_score','Weight'],['raw_count','Raw'],['momentum','Mom%'],['h1','H1'],['h2','H2'],['pred_score','Score']].map(([k,l]) => (
                    <th key={k} className="px-2 py-2 text-[9px] uppercase text-gray-600 cursor-pointer hover:bg-gray-100 text-center" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                  ))}
                  <th className="px-2 py-2 text-[9px] uppercase text-gray-600 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedDecay.slice(0, 20).map((n, idx) => (
                  <tr key={n.number} className={topDecay.has(n.number) ? 'bg-violet-50' : idx % 2 === 0 ? '' : 'bg-gray-50'} style={topDecay.has(n.number) ? {borderLeft: '3px solid #7c3aed'} : {}}>
                    <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                    <td className="px-2 py-1 border-b font-mono text-center">{n.weighted_score}</td>
                    <td className="px-2 py-1 border-b font-mono text-center">{n.raw_count}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center ${n.momentum > 0 ? 'text-green-600' : n.momentum < 0 ? 'text-red-500' : 'text-gray-400'}`}>{n.momentum > 0 ? '+' : ''}{n.momentum}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center ${n.h1 > n.h2 ? 'text-red-500' : 'text-gray-400'}`}>{n.h1}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.h2 > n.h1 ? 'text-green-600' : 'text-gray-400'}`}>{n.h2}</td>
                    <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.pred_score}</td>
                    <td className="px-2 py-1 border-b text-center"><StatusBadge status={n.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Rolling Window */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-700 text-white px-4 py-3">
            <h3 className="font-bold">{label} — Rolling Window (Last {windowSize})</h3>
            <p className="text-[10px] text-blue-200">Raw frequency in window, position + cross-ball momentum</p>
          </div>
          <div className="overflow-x-auto" data-testid="rolling-table">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50">
                <tr>
                  {[['number','#'],['recent_count',`R${windowSize}`],['h1','H1'],['h2','H2'],['delta','Delta%'],['cross_recent','XB'],['xb_h1','XH1'],['xb_h2','XH2'],['pred_score','Score']].map(([k,l]) => (
                    <th key={k} className="px-2 py-2 text-[9px] uppercase text-gray-600 cursor-pointer hover:bg-gray-100 text-center" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                  ))}
                  <th className="px-2 py-2 text-[9px] uppercase text-gray-600 text-center">Mom</th>
                </tr>
              </thead>
              <tbody>
                {sortedRw.slice(0, 20).map((n, idx) => (
                  <tr key={n.number} className={topRw.has(n.number) ? 'bg-blue-50' : idx % 2 === 0 ? '' : 'bg-gray-50'} style={topRw.has(n.number) ? {borderLeft: '3px solid #2563eb'} : {}}>
                    <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                    <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.recent_count}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center ${n.h1 > n.h2 ? 'text-red-500' : 'text-gray-400'}`}>{n.h1}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.h2 > n.h1 ? 'text-green-600' : 'text-gray-400'}`}>{n.h2}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center ${n.delta > 1 ? 'text-green-600' : n.delta < -1 ? 'text-red-500' : 'text-gray-400'}`}>{n.delta > 0 ? '+' : ''}{n.delta}%</td>
                    <td className="px-2 py-1 border-b font-mono text-center">{n.cross_recent}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center ${n.xb_h1 > n.xb_h2 ? 'text-red-500' : 'text-gray-400'}`}>{n.xb_h1}</td>
                    <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.xb_h2 > n.xb_h1 ? 'text-green-600' : 'text-gray-400'}`}>{n.xb_h2}</td>
                    <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.pred_score}</td>
                    <td className="px-2 py-1 border-b text-center"><MomentumBadge label={n.momentum_label} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Combined Prediction for this ball */}
      <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 text-white">
        <div className="flex items-center gap-2 mb-3"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Next {label} (Combined)</h3></div>
        <p className="text-xs text-white/70 mb-4">50% Decay + 50% Rolling Window. Purple highlight = top in Decay. Blue highlight = top in Rolling.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(() => {
            const decayMap = {};
            decayNums.forEach(n => { decayMap[n.number] = n; });
            const rwMap = {};
            rwNums.forEach(n => { rwMap[n.number] = n; });
            const allNums = new Set([...decayNums.map(n => n.number), ...rwNums.map(n => n.number)]);
            const merged = [];
            allNums.forEach(num => {
              const d = decayMap[num] || { pred_score: 0, half_momentum_label: 'FLAT' };
              const r = rwMap[num] || { pred_score: 0, momentum_label: 'FLAT' };
              merged.push({
                number: num,
                combined: Math.round((d.pred_score * 0.5 + r.pred_score * 0.5) * 100) / 100,
                decay: d.pred_score || 0,
                rolling: r.pred_score || 0,
                d_mom: d.half_momentum_label || 'FLAT',
                r_mom: r.momentum_label || 'FLAT',
                inTopDecay: topDecay.has(num),
                inTopRw: topRw.has(num),
              });
            });
            merged.sort((a, b) => b.combined - a.combined);
            return merged.slice(0, 10).map((n, idx) => (
              <div key={n.number} className={`p-3 rounded-lg ${idx === 0 ? 'bg-amber-400/25 ring-1 ring-amber-400' : 'bg-white/10'}`} data-testid={`combined-${n.number}`}>
                <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                <div className="text-center font-bold text-lg mt-1">{n.combined}</div>
                <div className="flex justify-center gap-2 text-[9px] mt-1">
                  <span className={`px-1 rounded ${n.inTopDecay ? 'bg-violet-400/40 text-violet-200' : 'text-white/40'}`}>D:{n.decay}</span>
                  <span className={`px-1 rounded ${n.inTopRw ? 'bg-blue-400/40 text-blue-200' : 'text-white/40'}`}>R:{n.rolling}</span>
                </div>
                <div className="flex justify-center gap-1 mt-1">
                  {n.d_mom === 'HEATING' && n.r_mom === 'HEATING' && <span className="text-[8px] font-bold text-orange-300 bg-orange-400/20 px-1 rounded">BOTH HOT</span>}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
}


export function Window21Content() {
  const [loading, setLoading] = useState(true);
  const [decayData, setDecayData] = useState(null);
  const [rwData, setRwData] = useState(null);
  const [btData, setBtData] = useState(null);
  const [windowSize, setWindowSize] = useState('20');
  const [mainTab, setMainTab] = useState("summary");

  useEffect(() => {
    (async () => {
      try {
        const [dRes, rwRes, btRes] = await Promise.all([
          axios.get(`${API}/analysis/decay-frequency`),
          axios.get(`${API}/analysis/rolling-window`),
          axios.get(`${API}/analysis/momentum-backtest`),
        ]);
        setDecayData(dRes.data);
        setRwData(rwRes.data);
        setBtData(btRes.data);
      } catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;

  return (
    <>
      <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 mb-6">
        <button onClick={() => setMainTab("summary")} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === "summary" ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-100"}`}>Predicted Next Ball</button>
        <button onClick={() => setMainTab("backtest")} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === "backtest" ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-100"}`}>Momentum Backtest</button>
        {Object.entries(ballLabels).map(([k, l]) => (
          <button key={k} onClick={() => setMainTab(k)} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === k ? "bg-[#002FA7] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{l}</button>
        ))}
      </div>
      {mainTab === "summary" && <PredictionSummary decayData={decayData} rwData={rwData} windowSize={windowSize} />}
      {mainTab === "backtest" && <MomentumBacktest data={btData} rwData={rwData} windowSize={windowSize} />}
      {Object.keys(ballLabels).includes(mainTab) && (
        <BallAnalysis ballKey={mainTab} label={ballLabels[mainTab]} decayData={decayData} rwData={rwData} windowSize={windowSize} />
      )}
    </>
  );
}
